import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { TodayLesson } from '../types';

interface TodayScheduleProps {
  lessons: TodayLesson[];
}

const TodaySchedule = ({ lessons }: TodayScheduleProps) => {
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in-progress':
        return 'bg-accent text-white';
      case 'completed':
        return 'bg-success text-white';
      case 'cancelled':
        return 'bg-error text-white';
      default:
        return 'bg-muted text-foreground';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in-progress':
        return 'În desfășurare';
      case 'completed':
        return 'Finalizată';
      case 'cancelled':
        return 'Anulată';
      default:
        return 'Urmează';
    }
  };

  if (lessons.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Orarul de azi</h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="Calendar"
            onClick={() => navigate('/class-management-hub?tab=orar')}
          >
            Vezi tot
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center py-8">
          <Icon name="CalendarOff" size={48} className="text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground text-center">Nu ai lecții programate azi</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Orarul de azi</h2>
        <Button
          variant="ghost"
          size="sm"
          iconName="Calendar"
          onClick={() => navigate('/class-management-hub?tab=orar')}
        >
          Vezi tot
        </Button>
      </div>

      <div className="space-y-3">
        {lessons.map((lesson) => (
          <div
            key={lesson.id}
            className="p-4 border border-border rounded-md hover:bg-muted transition-colors duration-150"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold text-foreground">{lesson.subject}</h3>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(
                      lesson.status || 'upcoming'
                    )}`}
                  >
                    {getStatusLabel(lesson.status || 'upcoming')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{lesson.day}</p>
                {lesson.classroomName && (
                  <p className="text-xs text-muted-foreground mt-0.5">{lesson.classroomName}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-xs text-foreground">
                  {lesson.start} - {lesson.end}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Tag" size={14} className="text-muted-foreground" />
                <span className="text-xs text-foreground">{lesson.type || '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={14} className="text-muted-foreground" />
                <span className="text-xs text-foreground">{lesson.room || '—'}</span>
              </div>
            </div>

            {lesson.comment && (
              <div className="flex items-center gap-2 mb-3">
                <Icon name="MessageSquareText" size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground line-clamp-2">{lesson.comment}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="xs"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => {
                  if (lesson.classroomId) navigate(`/class-management-hub?classId=${lesson.classroomId}&tab=orar`);
                  else navigate('/class-management-hub?tab=orar');
                }}
              >
                Deschide orarul
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TodaySchedule;
